import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  CalendarDays, Clock, AlertTriangle, CheckCircle2,
  Loader2, RefreshCw, Pencil, Save, X, ChevronDown, ChevronRight,
  Timer, Repeat, ShieldCheck, Sandwich, Shield, Info,
} from 'lucide-react';
import { LeaveBalanceView, PolicyValidationConfig } from '../../../../models/attendance';
import * as LeavesApi from '../../../../api/Attendance/leaves';
import { Roles } from '../../../../components/roles';
import Attendance from '../index';
import UserProfile from '../../index';
import { getApiErrorMessage } from '../../../../utils/apiErrors';

interface Props {
  userId: string;
  role: string;
}

function daysUntil(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const r = (n: number) => Math.round(n * 100) / 100;

const UserLeavePolicies = ({ userId, role }: Props) => {
  const { t } = useTranslation('leaves');
  const { id } = useParams();
  const profileUserId = id ?? userId;
  const isHr = Roles.HR.some((r: string) => r === role);

  const [balances, setBalances] = useState<LeaveBalanceView[]>([]);
  const [policyConfig, setPolicyConfig] = useState<PolicyValidationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // See EmployeeLeavesAndHolidays: never interpolate a missing id into the request path.
    if (!profileUserId) {
      setBalances([]);
      setError(t('userLeavePolicies.missingEmployee'));
      setLoading(false);
      return;
    }
    try {
      const [balanceRes, configRes] = await Promise.all([
        LeavesApi.getBalancePerUser(profileUserId),
        LeavesApi.getValidationConfig(profileUserId),
      ]);
      if (balanceRes?.errors?.length) {
        setError(getApiErrorMessage(balanceRes, t, t('userLeavePolicies.loadError')));
      } else {
        setBalances(balanceRes.payload ?? []);
      }
      if (!configRes?.errors?.length && configRes.payload) {
        setPolicyConfig(configRes.payload);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('userLeavePolicies.loadError')));
    } finally {
      setLoading(false);
    }
  }, [profileUserId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (leaveTypeId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(leaveTypeId)) {
        next.delete(leaveTypeId);
      } else {
        next.add(leaveTypeId);
      }
      return next;
    });
  };

  const handleStartEdit = (leaveTypeId: number) => {
    setEditingRow(leaveTypeId);
    setEditAmount('');
    setEditReason('');
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditAmount('');
    setEditReason('');
  };

  const handleSaveAdjustment = async (leaveTypeId: number) => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount === 0) return;
    setSaving(true);
    try {
      const result = await LeavesApi.adjustBalance({
        userId: profileUserId,
        leaveTypeId,
        amount,
        reason: editReason || undefined,
      });
      if (result?.errors?.length) {
        setError(getApiErrorMessage(result, t, t('userLeavePolicies.adjustError')));
      } else {
        await fetchData();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('userLeavePolicies.adjustError')));
    } finally {
      setSaving(false);
      setEditingRow(null);
      setEditAmount('');
      setEditReason('');
    }
  };

  // Hide Never-frequency leave types that have zero balance (HR hasn't granted days)
  const visibleBalances = balances.filter(b =>
    !(b.accrualFrequency === 'Never' && b.balance === 0)
  );

  const colCount = isHr ? 5 : 4;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="leave-policies-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-500">{t('userLeavePolicies.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-leave-policies-page">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Policy Info */}
      {policyConfig && policyConfig.policyId > 0 && (
        <div className="bg-card border border-border rounded-lg" data-testid="policy-info-section">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-blue-600" />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-md font-semibold text-gray-900">{policyConfig.policyName}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    policyConfig.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {policyConfig.isActive ? t('Active') : t('Inactive')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{t('userLeavePolicies.assignedPolicy')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Policy Assigned */}
      {!policyConfig && !loading && balances.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-2" data-testid="no-policy-notice">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-sm text-amber-700">{t('userLeavePolicies.noPolicy')}</span>
        </div>
      )}

      {/* Leave Balances Section */}
      {visibleBalances.length > 0 && (
        <div className="bg-card border border-border rounded-lg" data-testid="balance-section">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-600" />
              <h3 className="text-md font-semibold text-gray-900">
                {t('userLeavePolicies.leaveBalances')}
              </h3>
            </div>
            <button
              onClick={fetchData}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title={t('userLeavePolicies.refresh')}
              data-testid="refresh-balances"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="balance-table">
              <thead>
                <tr className="border-b border-border bg-gray-50">
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">{t('userLeavePolicies.leaveType')}</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium">{t('userLeavePolicies.eligibility')}</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium">{t('userLeavePolicies.balance')}</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium">{t('userLeavePolicies.carryForward')}</th>
                  {isHr && (
                    <th className="text-center py-3 px-4 text-gray-600 font-medium">{t('userLeavePolicies.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleBalances.map((b) => {
                  const isLow = b.warningThreshold != null && b.balance <= b.warningThreshold;
                  const isEditing = editingRow === b.leaveTypeId;
                  const isExpanded = expandedRows.has(b.leaveTypeId);

                  return (
                    <React.Fragment key={b.leaveTypeId}>
                      {/* Main row */}
                      <tr
                        className={`border-b border-border hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-gray-50' : ''}`}
                        onClick={() => toggleExpand(b.leaveTypeId)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                              : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                            }
                            <span className="font-medium text-gray-900">{t(b.leaveType)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-700">{r(b.eligibility)}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${isLow ? 'text-amber-600' : 'text-gray-900'}`}>
                            {r(b.balance)}
                          </span>
                          {isLow && (
                            <AlertTriangle className="inline-block h-3.5 w-3.5 text-amber-500 ml-1" />
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {b.carryForwardBalance > 0 ? (
                            <div>
                              <span className="text-gray-700">{r(b.carryForwardBalance)}</span>
                              {b.carryForwardExpiry && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                  <Clock className="inline-block h-3 w-3 mr-0.5" />
                                  {daysUntil(b.carryForwardExpiry) > 0
                                    ? t('userLeavePolicies.expiresIn', { days: daysUntil(b.carryForwardExpiry) })
                                    : t('userLeavePolicies.expired')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {isHr && (
                          <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                            {!isEditing && (
                              <button
                                onClick={() => handleStartEdit(b.leaveTypeId)}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title={t('userLeavePolicies.adjustBalance')}
                                data-testid={`adjust-balance-${b.leaveTypeId}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* Expanded details row */}
                      {isExpanded && (
                        <tr className="border-b border-border bg-slate-50">
                          <td colSpan={colCount} className="py-4 px-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ps-6">
                              {/* Validation Rules */}
                              <div className="p-3 bg-card rounded-lg border border-border">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />
                                  <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.minNoticeDays')}</span>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {b.minNoticeDays > 0
                                    ? t('userLeavePolicies.nDays', { count: b.minNoticeDays })
                                    : t('userLeavePolicies.none')}
                                </div>
                              </div>

                              <div className="p-3 bg-card rounded-lg border border-border">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <CalendarDays className="h-3.5 w-3.5 text-amber-500" />
                                  <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.maxConsecutiveDays')}</span>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {b.maxConsecutiveDays != null && b.maxConsecutiveDays > 0
                                    ? t('userLeavePolicies.nDays', { count: b.maxConsecutiveDays })
                                    : t('userLeavePolicies.unlimited')}
                                </div>
                              </div>

                              <div className="p-3 bg-card rounded-lg border border-border">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Sandwich className="h-3.5 w-3.5 text-purple-500" />
                                  <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.sandwichRule')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {b.sandwichRuleEnabled ? (
                                    <>
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                      <span className="text-sm font-medium text-green-700">{t('userLeavePolicies.enabled')}</span>
                                    </>
                                  ) : (
                                    <span className="text-sm font-medium text-gray-900">{t('userLeavePolicies.disabled')}</span>
                                  )}
                                </div>
                              </div>

                              {/* Accrual Info */}
                              {b.accrualFrequency && (
                                <div className="p-3 bg-card rounded-lg border border-border">
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Repeat className="h-3.5 w-3.5 text-indigo-500" />
                                    <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.accrualFrequency')}</span>
                                  </div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {t(b.accrualFrequency)}
                                  </div>
                                </div>
                              )}

                              {b.accrualRate != null && (
                                <div className="p-3 bg-card rounded-lg border border-border">
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Repeat className="h-3.5 w-3.5 text-indigo-500" />
                                    <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.accrualRate')}</span>
                                  </div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {r(b.accrualRate)}
                                  </div>
                                </div>
                              )}

                              {/* Renewal Date */}
                              {b.renewalDate && (
                                <div className="p-3 bg-card rounded-lg border border-border">
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Timer className="h-3.5 w-3.5 text-teal-500" />
                                    <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.renewalDate')}</span>
                                  </div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatDate(b.renewalDate)}
                                  </div>
                                </div>
                              )}

                              {/* Include Non-Working Days */}
                              <div className="p-3 bg-card rounded-lg border border-border">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <CalendarDays className="h-3.5 w-3.5 text-gray-500" />
                                  <span className="text-xs font-medium text-gray-500">{t('userLeavePolicies.includeNonWorkingDays')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {b.includeNonWorkingrDays ? (
                                    <>
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                      <span className="text-sm font-medium text-green-700">{t('userLeavePolicies.enabled')}</span>
                                    </>
                                  ) : (
                                    <span className="text-sm font-medium text-gray-900">{t('userLeavePolicies.disabled')}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Inline adjustment row */}
                      {isEditing && (
                        <tr className="bg-blue-50 border-b border-blue-200" data-testid={`adjust-row-${b.leaveTypeId}`}>
                          <td colSpan={colCount} className="py-3 px-4">
                            <div className="flex items-end gap-3 flex-wrap">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {t('userLeavePolicies.adjustmentAmount')}
                                </label>
                                <input
                                  type="number"
                                  step="0.5"
                                  value={editAmount}
                                  onChange={(e) => setEditAmount(e.target.value)}
                                  className="w-28 px-2 py-1.5 text-sm border border-border rounded-md bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                                  placeholder="+/-"
                                  autoFocus
                                  data-testid="adjustment-amount-input"
                                />
                                <div className="text-xs text-gray-400 mt-0.5">
                                  {t('userLeavePolicies.adjustmentHint')}
                                </div>
                              </div>
                              <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {t('userLeavePolicies.reason')}
                                </label>
                                <input
                                  type="text"
                                  value={editReason}
                                  onChange={(e) => setEditReason(e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-card focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                                  placeholder={t('userLeavePolicies.reasonPlaceholder')}
                                  data-testid="adjustment-reason-input"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleSaveAdjustment(b.leaveTypeId)}
                                  disabled={saving || !editAmount || parseFloat(editAmount) === 0}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid="save-adjustment"
                                >
                                  {saving ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Save className="h-3.5 w-3.5" />
                                  )}
                                  {t('userLeavePolicies.save')}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-card border border-border rounded-md hover:bg-gray-50 disabled:opacity-50"
                                  data-testid="cancel-adjustment"
                                >
                                  <X className="h-3.5 w-3.5" />
                                  {t('userLeavePolicies.cancel')}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const UserProfileLeavePolicies = (props: Props) => {
  return (
    <UserProfile>
      <Attendance>
        <UserLeavePolicies {...props} />
      </Attendance>
    </UserProfile>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  role: state.user.role,
});

export { UserLeavePolicies };
export default connect(mapStateToProps)(UserProfileLeavePolicies);
